export enum Holiday {
	FourthOfJuly = 'FourthOfJuly',
	Thanksgiving = 'Thanksgiving',
	NewYear = 'NewYear',
	Valentine = 'Valentine',
	Christmas = 'Christmas',
	Halloween = 'Halloween',
}

interface MonthAndDay {
	readonly month: number;
	readonly day: number;
}

type MonthAndDayForYear = (year: number) => MonthAndDay;

// Function boundaries are resolved with the input date's own year, so a single
// function-based window must stay within one calendar year. Year-spanning
// windows (e.g. New Year) must use static MonthAndDay boundaries instead.
type HolidayBoundary = MonthAndDay | MonthAndDayForYear;

interface HolidayWindow {
	readonly holiday: Holiday;
	readonly start: HolidayBoundary;
	readonly end: HolidayBoundary;
}

const NOVEMBER_MONTH_NUMBER = 11;
const THURSDAY_WEEKDAY_NUMBER = 4;
const DAYS_IN_WEEK = 7;
const WEEKS_BEFORE_FOURTH_THURSDAY = 3;
const THANKSGIVING_LEAD_IN_DAYS = 3;
const THANKSGIVING_TRAILING_DAYS = 1;

function getFourthThursdayOfNovemberDay(year: number): number {
	const firstOfNovember = new Date(year, NOVEMBER_MONTH_NUMBER - 1, 1);
	const daysUntilFirstThursday = (THURSDAY_WEEKDAY_NUMBER - firstOfNovember.getDay() + DAYS_IN_WEEK) % DAYS_IN_WEEK;
	const firstThursdayDay = 1 + daysUntilFirstThursday;

	return firstThursdayDay + WEEKS_BEFORE_FOURTH_THURSDAY * DAYS_IN_WEEK;
}

const HOLIDAY_WINDOWS = [
	{ holiday: Holiday.NewYear, start: { month: 12, day: 29 }, end: { month: 1, day: 2 } },
	{ holiday: Holiday.Valentine, start: { month: 2, day: 10 }, end: { month: 2, day: 14 } },
	{ holiday: Holiday.FourthOfJuly, start: { month: 7, day: 2 }, end: { month: 7, day: 4 } },
	{ holiday: Holiday.Halloween, start: { month: 10, day: 25 }, end: { month: 10, day: 31 } },
	{ holiday: Holiday.Christmas, start: { month: 12, day: 22 }, end: { month: 12, day: 26 } },
	{
		holiday: Holiday.Thanksgiving,
		start: (year: number) => ({
			month: NOVEMBER_MONTH_NUMBER,
			day: getFourthThursdayOfNovemberDay(year) - THANKSGIVING_LEAD_IN_DAYS,
		}),
		end: (year: number) => ({
			month: NOVEMBER_MONTH_NUMBER,
			day: getFourthThursdayOfNovemberDay(year) + THANKSGIVING_TRAILING_DAYS,
		}),
	},
] as const satisfies ReadonlyArray<HolidayWindow>;

function getMonthAndDay(date: Date): MonthAndDay {
	return { month: date.getMonth() + 1, day: date.getDate() };
}

function toMonthAndDayKey(monthAndDay: MonthAndDay): number {
	return monthAndDay.month * 100 + monthAndDay.day;
}

function resolveBoundary(boundary: HolidayBoundary, year: number): MonthAndDay {
	return typeof boundary === 'function' ? boundary(year) : boundary;
}

function isDateWithinWindow(date: Date, window: HolidayWindow): boolean {
	const year = date.getFullYear();
	const key = toMonthAndDayKey(getMonthAndDay(date));
	const startKey = toMonthAndDayKey(resolveBoundary(window.start, year));
	const endKey = toMonthAndDayKey(resolveBoundary(window.end, year));

	if (startKey <= endKey) {
		return key >= startKey && key <= endKey;
	}

	return key >= startKey || key <= endKey;
}

export function getActiveHolidayForDate(date: Date): Holiday | null {
	const maybeHolidayWindow = HOLIDAY_WINDOWS.find((window) => isDateWithinWindow(date, window));

	return maybeHolidayWindow ? maybeHolidayWindow.holiday : null;
}
